import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { eventTicketAction } from 'api'
import { getAvatarUrl } from '../../../../api/utils'
import { 
  X, 
  ExternalLink, 
  User, 
  Heart, 
  BookOpen, 
  Share2, 
  Check,
  Star,
  MessageCircle
} from 'lucide-react'
import {
  ModalOverlay,
  ModalContent,
  CloseButton,
  HeaderSection,
  AvatarContainer,
  Avatar,
  StatusIndicator,
  UserName,
  UserAlias,
  VoteSection,
  VoteText,
  VoteSwitch,
  ContentSection,
  InfoCard,
  InfoTitle,
  InfoContent,
  TagsContainer,
  Tag,
  FooterSection,
  ContactButton,
  PortfolioButton,
  EmptyState,
  LoadingContainer
} from './styled'

const EventModalParticipant = ({ selectedParticipant, myTicket, onClose, visibleModal }) => {
  const [actionSelected, setActionSelected] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [ticketAction, setTicketAction] = useState({
    origin: myTicket?.slug,
    target: selectedParticipant?.slug,
    type: 'VT',
    value: 'vote',
  })

  const updateAction = async () => {
    setIsLoading(true)
    try {
      await eventTicketAction(ticketAction)
      setActionSelected(!actionSelected)
    } catch (error) {
      console.error('The error is -> ', error)
    } finally {
      setIsLoading(false)
    }
  }

  const isValidURL = (url) => {
    const pattern = /^((https?|ftp):\/\/)?[\w-]+(\.[\w-]+)+([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?$/
    return pattern.test(url)
  }

  const openWebsite = (url) => {
    if (url) {
      if (isValidURL(url)) {
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = 'https://' + url
        }
      }
      window.open(url, '_blank')
    }
  }

  useEffect(() => {
    if (visibleModal && selectedParticipant && myTicket) {
      setTicketAction({ 
        ...ticketAction, 
        origin: myTicket?.slug, 
        target: selectedParticipant?.slug 
      })
      
      const actionFound = selectedParticipant?.actions?.some(action => {
        return myTicket?.slug === action.origin && selectedParticipant?.slug === action.target
      }) || false
      
      setActionSelected(actionFound)
    }
  }, [visibleModal, selectedParticipant, myTicket])

  if (!selectedParticipant) {
    return null
  }

  const avatarSrc = getAvatarUrl(selectedParticipant?.user, null)

  const getInitials = () => {
    const firstName = selectedParticipant?.user?.first_name || ''
    const lastName = selectedParticipant?.user?.last_name || ''
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase()
  }

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (isLoading) {
    return (
      <ModalOverlay open={visibleModal} onClick={handleOverlayClick}>
        <LoadingContainer>
          <div className="spinner" />
        </LoadingContainer>
      </ModalOverlay>
    )
  }

  return (
    <ModalOverlay open={visibleModal} onClick={handleOverlayClick}>
      <ModalContent>
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>

        <HeaderSection>
          <AvatarContainer>
            <Avatar src={avatarSrc}>
              {!avatarSrc && getInitials()}
            </Avatar>
            {selectedParticipant?.status === 'AC' && (
              <StatusIndicator>
                <Check size={12} color="white" />
              </StatusIndicator>
            )}
          </AvatarContainer>
          
          <UserName>
            {selectedParticipant?.user?.first_name} {selectedParticipant?.user?.last_name}
          </UserName>
          
          {selectedParticipant?.user?.alias && (
            <UserAlias>
              {selectedParticipant.user.alias}
            </UserAlias>
          )}
        </HeaderSection>

        {/* Vote Section - Only show if myTicket exists and is not 'imposible' */}
        {myTicket && myTicket !== 'imposible' && (
          <VoteSection>
            <Heart size={20} color="#8b5cf6" />
            <VoteText>Great Chat!</VoteText>
            <VoteSwitch 
              checked={actionSelected} 
              onClick={updateAction}
            />
          </VoteSection>
        )}

        <ContentSection>
          {/* About Me Section */}
          {selectedParticipant?.user?.description && (
            <InfoCard>
              <InfoTitle color="#10b981">
                <User size={18} />
                About Me
              </InfoTitle>
              <InfoContent>
                {selectedParticipant.user.description}
              </InfoContent>
            </InfoCard>
          )}

          {/* Learning Interests */}
          {selectedParticipant?.needs && selectedParticipant.needs.length > 0 && (
            <InfoCard>
              <InfoTitle color="#3b82f6">
                <BookOpen size={18} />
                Happy to learn about
              </InfoTitle>
              <TagsContainer>
                {selectedParticipant.needs.map((item, key) => (
                  <Tag key={key} variant="needs">
                    {item}
                  </Tag>
                ))}
              </TagsContainer>
            </InfoCard>
          )}

          {/* Knowledge Sharing */}
          {selectedParticipant?.offers && selectedParticipant.offers.length > 0 && (
            <InfoCard>
              <InfoTitle color="#10b981">
                <Share2 size={18} />
                Happy to share my knowledge in
              </InfoTitle>
              <TagsContainer>
                {selectedParticipant.offers.map((item, key) => (
                  <Tag key={key} variant="offers">
                    {item}
                  </Tag>
                ))}
              </TagsContainer>
            </InfoCard>
          )}

          {/* Empty state if no information */}
          {!selectedParticipant?.user?.description && 
           (!selectedParticipant?.needs || selectedParticipant.needs.length === 0) &&
           (!selectedParticipant?.offers || selectedParticipant.offers.length === 0) && (
            <EmptyState>
              <MessageCircle size={48} color="rgba(255, 255, 255, 0.3)" />
              <div style={{ marginTop: '16px' }}>
                This participant hasn't shared any information yet.
              </div>
            </EmptyState>
          )}
        </ContentSection>

        {/* Action Buttons */}
        {(selectedParticipant?.link_1 || selectedParticipant?.link_2) && (
          <FooterSection>
            {selectedParticipant?.link_1 && (
              <ContactButton onClick={() => openWebsite(selectedParticipant.link_1)}>
                <MessageCircle size={18} />
                Contact Me
                <ExternalLink size={16} />
              </ContactButton>
            )}
            
            {selectedParticipant?.link_2 && (
              <PortfolioButton onClick={() => openWebsite(selectedParticipant.link_2)}>
                <Star size={18} />
                What I do
                <ExternalLink size={16} />
              </PortfolioButton>
            )}
          </FooterSection>
        )}
      </ModalContent>
    </ModalOverlay>
  )
}

EventModalParticipant.propTypes = {
  selectedParticipant: PropTypes.object,
  myTicket: PropTypes.object,
  onClose: PropTypes.func,
  visibleModal: PropTypes.bool,
}

EventModalParticipant.defaultProps = {
  selectedParticipant: null,
  myTicket: null,
  onClose: () => {},
  visibleModal: false,
}

export default EventModalParticipant